import {
  ExternalLink, ShoppingBag, Mic, Church, Store, Music, BookOpen, Sparkles,
  Instagram, Youtube, Linkedin, Facebook, Mail, Phone, Calendar, ArrowRight, Search, X,
} from "lucide-react";
import { useState, useMemo, useEffect, useRef } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/hooks/useReveal";
import { EMAIL, PHONE_DISPLAY, PHONE_HREF } from "@/content/site";
import { trackCTA } from "@/lib/tracking";

type LinkItem = {
  title: string;
  desc: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  to?: boolean; // internal route
};

const PRIMARY: LinkItem[] = [
  {
    title: "Book Dr. Sully to Speak",
    desc: "Keynotes, workshops, panels, multi-day residencies.",
    href: "/speaking",
    icon: Mic,
    badge: "Booking",
    to: true,
  },
  {
    title: "Merch · DrSullySpeaks Clothing",
    desc: "Official tees, hoodies & gear. Wear the message.",
    href: "https://saysosociety.com/collections/drsullyspeaks-merch",
    icon: ShoppingBag,
    badge: "Shop",
  },
  {
    title: "Honest Kingdom Voice · The Experience",
    desc: "The full HKV experience — manifesto, episodes, and the call.",
    href: "/honest-kingdom-voice",
    icon: Sparkles,
    badge: "Featured",
    to: true,
  },
  {
    title: "Honest Kingdom Voice · Podcast",
    desc: "Listen on Spotify. New episodes weekly.",
    href: "https://open.spotify.com/show/6t5G1oDtkligs6wi4iGgvZ",
    icon: Mic,
    badge: "Listen",
  },
  {
    title: "Anchored SA · The Church",
    desc: "San Antonio Community Church. Visit & connect.",
    href: "https://www.anchoredsa.com",
    icon: Church,
    badge: "Ministry",
  },
];

const PROGRAMS: LinkItem[] = [
  {
    title: "Opps-Finder",
    desc: "2-minute diagnostic — find your fundable program lane.",
    href: "/scan",
    icon: Sparkles,
    to: true,
  },
  {
    title: "The Framework",
    desc: "The signature methodology behind every engagement.",
    href: "/framework",
    icon: BookOpen,
    to: true,
  },
  {
    title: "Work With Dr. Sully",
    desc: "Speaking, coaching, or consulting inquiry.",
    href: "/inquire",
    icon: Calendar,
    to: true,
  },
];

const SHOP: LinkItem[] = [
  {
    title: "Say So Society · Global Co.",
    desc: "All collections — apparel, drops, lifestyle.",
    href: "https://saysosociety.com/collections",
    icon: Store,
  },
  {
    title: "Stan Store",
    desc: "Coaching, products & resources in one place.",
    href: "https://stan.store/DrSullySpeaks",
    icon: Sparkles,
  },
];

const MEDIA: LinkItem[] = [
  {
    title: "Music · @DrSullySpeaks",
    desc: "On YouTube Music — and every major platform.",
    href: "https://music.youtube.com/channel/UClW-Fv3VHRzaB5YQtkyhuGA",
    icon: Music,
  },
  {
    title: "Substack — Essays & Sermons",
    desc: "Long-form writing straight to your inbox.",
    href: "https://substack.com/@drsullyspeaks",
    icon: BookOpen,
  },
];

const SOCIAL: { label: string; href: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { label: "Instagram · @DrSullySpeaks", href: "https://instagram.com/DrSullySpeaks", icon: Instagram },
  { label: "TikTok · @DrSullySpeaks", href: "https://tiktok.com/@DrSullySpeaks", icon: Music },
  { label: "YouTube · @DrSullySpeaks", href: "https://youtube.com/@DrSullySpeaks", icon: Youtube },
  { label: "X / Twitter · @DrSullySpeaks", href: "https://x.com/DrSullySpeaks", icon: ExternalLink },
  { label: "LinkedIn · Dr. Sully", href: "https://www.linkedin.com/in/drsullyspeaks/", icon: Linkedin },
  { label: "Facebook · DrSullySpeak", href: "https://facebook.com/DrSullySpeak", icon: Facebook },
];

const Card = ({ item, primary = false }: { item: LinkItem; primary?: boolean }) => {
  const Icon = item.icon;
  const isExternal = !item.to;
  const linkProps = isExternal
    ? { href: item.href, target: "_blank", rel: "noopener noreferrer" as const }
    : { href: item.href };
  return (
    <a
      {...linkProps}
      onClick={() =>
        trackCTA("links_card_click", {
          title: item.title,
          href: item.href,
          external: isExternal,
          badge: item.badge,
        })
      }
      className={`group relative flex h-full border border-border bg-navy-elevated/60 hover:bg-navy-elevated hover:border-gold hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_hsl(var(--gold)/0.5)] active:translate-y-0 active:scale-[0.985] active:border-gold active:bg-gold/15 active:shadow-[inset_0_0_0_1px_hsl(var(--gold))] transition-all duration-200 p-6 outline-none focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep overflow-hidden touch-manipulation ${
        primary ? "md:p-8" : ""
      }`}
    >
      <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-0 bg-gold group-hover:w-1 group-focus-visible:w-1 group-active:w-1.5 transition-all duration-200" />
      <div className="flex items-start gap-4 w-full">
        <div className="shrink-0 w-12 h-12 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold/20 group-hover:border-gold group-active:bg-gold/30 transition-colors">
          <Icon className="w-5 h-5 text-gold" />
        </div>
        <div className="flex-1 min-w-0">
          {item.badge && (
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">{item.badge}</div>
          )}
          <div className="flex items-center gap-2">
            <h3 className={`font-display uppercase tracking-[0.12em] text-white group-hover:text-gold group-active:text-gold transition-colors ${primary ? "text-lg md:text-xl" : "text-base"}`}>
              {item.title}
            </h3>
            {isExternal ? (
              <ExternalLink className="w-3.5 h-3.5 text-silver/50 group-hover:text-gold transition-colors shrink-0" aria-hidden="true" />
            ) : (
              <ArrowRight className="w-3.5 h-3.5 text-silver/50 group-hover:text-gold group-hover:translate-x-0.5 transition-all shrink-0" aria-hidden="true" />
            )}
          </div>
          <p className="text-sm text-silver mt-2 leading-relaxed">{item.desc}</p>
        </div>
      </div>
    </a>
  );
};

type CategoryKey = "all" | "featured" | "programs" | "shop" | "media" | "social" | "contact";
const CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "featured", label: "Featured" },
  { key: "programs", label: "Programs" },
  { key: "shop", label: "Shop" },
  { key: "media", label: "Media" },
  { key: "social", label: "Social" },
  { key: "contact", label: "Contact" },
];

const SectionHeader = ({ label, title, desc }: { label: string; title: string; desc?: string }) => (
  <div className="text-center mb-8">
    <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">{label}</div>
    <h2 className="font-display uppercase text-white text-xl md:text-2xl tracking-wide">{title}</h2>
    {desc && <p className="text-silver text-sm mt-3 max-w-xl mx-auto leading-relaxed">{desc}</p>}
  </div>
);

const Links = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<CategoryKey>(
    (CATEGORIES.find((c) => c.key === searchParams.get("cat"))?.key as CategoryKey) ?? "all",
  );
  const [query, setQuery] = useState(searchParams.get("q") ?? "");
  const searchRef = useRef<HTMLInputElement>(null);

  // Persist query + category in URL (shallow, replace) so links are shareable.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (query) next.set("q", query); else next.delete("q");
    if (filter !== "all") next.set("cat", filter); else next.delete("cat");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [query, filter, searchParams, setSearchParams]);

  // Keyboard: "/" focuses search (when not already typing), Escape clears.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      const typing = tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement | null)?.isContentEditable;
      if (e.key === "/" && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "Escape" && document.activeElement === searchRef.current) {
        setQuery("");
        searchRef.current?.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (item: LinkItem) =>
    !q || item.title.toLowerCase().includes(q) || item.desc.toLowerCase().includes(q);
  const matchesSocial = (s: { label: string }) => !q || s.label.toLowerCase().includes(q);

  const primary = PRIMARY.filter(matches);
  const programs = PROGRAMS.filter(matches);
  const shop = SHOP.filter(matches);
  const media = MEDIA.filter(matches);
  const social = SOCIAL.filter(matchesSocial);

  const showSection = (key: CategoryKey, count: number) =>
    (filter === "all" || filter === key) && (q ? count > 0 : true);
  const show = (key: CategoryKey) => filter === "all" || filter === key;

  // Anchor IDs used by the filter buttons to smooth-scroll
  const sectionId = useMemo(
    () => ({
      featured: "section-featured",
      programs: "section-programs",
      shop: "section-shop",
      media: "section-media",
      social: "section-social",
      contact: "section-contact",
    }),
    [],
  );

  const handleFilter = (key: CategoryKey) => {
    setFilter(key);
    trackCTA("links_filter", { category: key });
    if (key !== "all") {
      const id = sectionId[key as Exclude<CategoryKey, "all">];
      requestAnimationFrame(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  const totalMatches = primary.length + programs.length + shop.length + media.length + social.length;

  return (
    <div className="min-h-screen bg-navy-deep">
      <PageMeta
        title="Links · DrSullySpeaks"
        description="Every link in one place — booking, church, podcast, merch, music, socials, and contact for Dr. Sully."
        path="/links"
      />
      <SiteNav />

      <main id="main" className="container pt-32 pb-20">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs uppercase tracking-[0.35em] text-gold mb-4">All Things DrSullySpeaks</div>
            <h1 className="font-display uppercase tracking-[0.08em] text-white text-3xl md:text-5xl leading-tight">
              The <span className="text-orange">Links</span> Page
            </h1>
            <p className="text-silver mt-5 leading-relaxed">
              Booking. Church. Podcast. Merch. Music. Socials. Everything in one place.
            </p>
          </div>
        </Reveal>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-5">
          <label htmlFor="links-search" className="sr-only">Search links</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-silver/70" aria-hidden="true" />
            <input
              id="links-search"
              ref={searchRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search links — podcast, merch, speaking…"
              autoComplete="off"
              spellCheck={false}
              enterKeyHint="search"
              aria-describedby="links-search-hint"
              className="w-full bg-navy-elevated/60 border border-border focus:border-gold/70 text-white placeholder:text-silver/60 text-sm pl-10 pr-20 py-3 outline-none focus-visible:ring-2 focus-visible:ring-gold/70 transition-colors rounded-sm"
            />
            <kbd
              aria-hidden="true"
              className="hidden md:flex absolute right-10 top-1/2 -translate-y-1/2 items-center justify-center min-w-5 h-5 px-1.5 text-[10px] font-mono text-silver/80 bg-navy-deep border border-border rounded"
            >
              /
            </kbd>
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => { setQuery(""); searchRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-silver hover:text-white rounded outline-none focus-visible:ring-2 focus-visible:ring-gold/70 active:scale-90 transition-transform"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div id="links-search-hint" className="text-[11px] uppercase tracking-[0.22em] text-silver/70 mt-2 text-center">
            {q ? (
              <>{totalMatches} match{totalMatches === 1 ? "" : "es"} for "{query}" · <kbd className="font-mono normal-case tracking-normal">Esc</kbd> to clear</>
            ) : (
              <>Press <kbd className="font-mono normal-case tracking-normal">/</kbd> to search</>
            )}
          </div>
        </div>

        {/* Category filters */}
        <div
          role="tablist"
          aria-label="Filter links by category"
          className="sticky top-[68px] z-30 -mx-4 px-4 py-3 bg-navy-deep/85 backdrop-blur-md border-y border-border mb-10 flex flex-wrap justify-center gap-2"
        >
          {CATEGORIES.map((c) => {
            const active = filter === c.key;
            return (
              <button
                key={c.key}
                role="tab"
                aria-selected={active}
                onClick={() => handleFilter(c.key)}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border transition-all outline-none focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep touch-manipulation active:scale-[0.97] ${
                  active
                    ? "bg-gold text-navy-deep border-gold shadow-[0_6px_20px_-8px_hsl(var(--gold)/0.7)]"
                    : "bg-transparent text-silver border-border hover:text-white hover:border-gold/60 active:bg-gold/10 active:border-gold"
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Empty state */}
        {q && totalMatches === 0 && (
          <div className="max-w-xl mx-auto text-center border border-border bg-navy-elevated/40 py-10 px-6 mb-10">
            <p className="text-white font-display uppercase tracking-[0.18em] text-sm">No matches</p>
            <p className="text-silver text-sm mt-2">Try a broader term, or <button onClick={() => setQuery("")} className="text-gold hover:underline">clear search</button>.</p>
          </div>
        )}

        {/* PRIMARY / Featured */}
        {showSection("featured", primary.length) && (
          <section id="section-featured" className="grid gap-4 md:gap-5 max-w-3xl mx-auto scroll-mt-32">
            {primary.map((item) => (
              <Reveal key={item.href}><Card item={item} primary /></Reveal>
            ))}
          </section>
        )}

        {showSection("featured", primary.length) && <div className="gold-divider opacity-40 max-w-3xl mx-auto my-14" />}

        {/* PROGRAMS */}
        {showSection("programs", programs.length) && (
          <section id="section-programs" className="max-w-3xl mx-auto mb-14 scroll-mt-32">
            <SectionHeader
              label="Work & Programs"
              title="Engage with Dr. Sully"
              desc="Diagnostics, frameworks, and the direct path to working together."
            />
            <div className="grid gap-4 md:grid-cols-3 items-stretch">
              {programs.map((item) => (
                <Reveal key={item.href}><Card item={item} /></Reveal>
              ))}
            </div>
          </section>
        )}

        {/* In-page reading CTA */}
        {!q && (filter === "all" || filter === "programs") && (
          <Reveal>
            <aside
              className="max-w-3xl mx-auto mb-14 relative overflow-hidden border border-gold/40 bg-gradient-to-br from-gold/10 via-navy-elevated/60 to-navy-deep p-7 md:p-9 rounded-sm"
              aria-label="Featured call to action"
            >
              <div className="absolute inset-y-0 left-0 w-1 bg-gold" aria-hidden="true" />
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Ready when you are</div>
                  <h3 className="font-display uppercase tracking-[0.1em] text-white text-xl md:text-2xl">
                    Skip the scroll — book a discovery call.
                  </h3>
                  <p className="text-silver text-sm mt-2 max-w-md leading-relaxed">
                    Speaking, coaching, or consulting — start the conversation in under 60 seconds.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                  <RouterLink
                    to="/inquire"
                    onClick={() => trackCTA("links_inline_cta_inquire", { surface: "links_inline_cta" })}
                    className="bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 active:scale-[0.98] transition-all text-center inline-flex flex-col items-center leading-tight touch-manipulation"
                  >
                    <span>Work With Dr. Sully</span>
                    <span className="text-[10px] tracking-[0.18em] text-white/85 mt-0.5">{PHONE_DISPLAY}</span>
                  </RouterLink>
                  <a
                    href={PHONE_HREF}
                    onClick={() => trackCTA("phone_click", { surface: "links_inline_cta" })}
                    className="border border-gold/60 text-white hover:bg-gold/10 hover:border-gold active:bg-gold/15 active:scale-[0.98] font-display uppercase tracking-[0.2em] text-xs px-6 py-4 transition-all text-center touch-manipulation"
                  >
                    Call Now
                  </a>
                </div>
              </div>
            </aside>
          </Reveal>
        )}

        {/* SHOP */}
        {showSection("shop", shop.length) && (
          <section id="section-shop" className="max-w-3xl mx-auto mb-14 scroll-mt-32">
            <SectionHeader
              label="Shop"
              title="Merch, Books & Drops"
              desc="Wear the message. Read the work. Carry the movement."
            />
            <div className="grid gap-4 md:grid-cols-2 items-stretch">
              {shop.map((item) => (
                <Reveal key={item.href}><Card item={item} /></Reveal>
              ))}
            </div>
          </section>
        )}

        {/* MEDIA */}
        {showSection("media", media.length) && (
          <section id="section-media" className="max-w-3xl mx-auto mb-14 scroll-mt-32">
            <SectionHeader
              label="Media"
              title="Listen, Read & Watch"
              desc="Honest Kingdom Voice on every platform — plus essays, music, and long-form."
            />
            <div className="grid gap-4 md:grid-cols-2 items-stretch">
              {media.map((item) => (
                <Reveal key={item.href}><Card item={item} /></Reveal>
              ))}
            </div>
          </section>
        )}

        {/* SOCIAL */}
        {showSection("social", social.length) && (
          <section id="section-social" className="max-w-3xl mx-auto mb-14 scroll-mt-32">
            <SectionHeader label="Follow" title="On the Socials" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {social.map((s) => (
                <a
                  key={s.href}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCTA("social_click", { label: s.label, href: s.href })}
                  className="group flex items-center gap-3 border border-border bg-navy-elevated/40 hover:bg-navy-elevated hover:border-gold hover:-translate-y-0.5 active:bg-gold/10 active:border-gold active:scale-[0.985] transition-all p-4 outline-none focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-deep touch-manipulation"
                >
                  <div className="shrink-0 w-9 h-9 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <s.icon className="w-4 h-4 text-gold" />
                  </div>
                  <span className="text-white text-sm flex-1">{s.label}</span>
                  <ExternalLink className="w-3.5 h-3.5 text-silver/50 group-hover:text-gold transition-colors" />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* CONTACT */}
        {show("contact") && !q && (
          <section id="section-contact" className="max-w-3xl mx-auto scroll-mt-32">
            <SectionHeader label="Contact" title="Reach Out Direct" />
            <div className="grid gap-3 sm:grid-cols-2">
              <a
                href={`mailto:${EMAIL}`}
                onClick={() => trackCTA("email_click", { surface: "links_contact" })}
                className="group flex items-center gap-3 border border-border bg-navy-elevated/40 hover:bg-navy-elevated hover:border-gold active:bg-gold/10 active:scale-[0.985] transition-all p-4 touch-manipulation"
              >
                <div className="shrink-0 w-9 h-9 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-gold" />
                </div>
                <span className="text-white text-sm">{EMAIL}</span>
              </a>
              <a
                href={PHONE_HREF}
                onClick={() => trackCTA("phone_click", { surface: "links_contact" })}
                className="group flex items-center gap-3 border border-border bg-navy-elevated/40 hover:bg-navy-elevated hover:border-gold active:bg-gold/10 active:scale-[0.985] transition-all p-4 touch-manipulation"
              >
                <div className="shrink-0 w-9 h-9 rounded-md bg-gold/10 border border-gold/30 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-gold" />
                </div>
                <span className="text-white text-sm">{PHONE_DISPLAY}</span>
              </a>
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
    </div>
  );
};


export default Links;
